// src/contexts/auth/hooks/auth-dev.ts
/** @jsxImportSource @emotion/react */

import { useCallback, useMemo } from "react";

import type { RegistrationData, Session, User } from "../types";
import { clearStoredTokens, readStoredTokens } from "../helpers";
import { makeAuthLogger } from "./storage";

export const useAuthDev = (deps: {
    session: Session | null;
    user: User | null;
    userId: string | undefined;
    onboarding: RegistrationData;
    currentStepState: number;

    setSession: (v: Session | null) => void;
    setUser: (v: User | null) => void;
    setUserId: (v: string | undefined) => void;
    setRegistrationState: React.Dispatch<
        React.SetStateAction<RegistrationData>
    >;
    setCurrentStepState: (v: number) => void;
    setError: (v: string | null) => void;

    signOut: () => Promise<void>;
    emitAuthChange: () => void;
}) => {
    const {
        session,
        user,
        userId,
        onboarding,
        currentStepState,
        setSession,
        setUser,
        setUserId,
        setRegistrationState,
        setCurrentStepState,
        setError,
        signOut,
        emitAuthChange,
    } = deps;

    const L = makeAuthLogger("dev");

    const devTokens = useMemo(() => {
        const t = readStoredTokens();
        const now_sec = Math.floor(Date.now() / 1000);
        const is_expired = t.expires_at > 0 ? now_sec >= t.expires_at : false;

        const snapshot = {
            access_token_present: !!t.access_token,
            refresh_token_present: !!t.refresh_token,
            expires_at: t.expires_at,
            now_sec,
            is_expired,
        };

        L.log("[devTokens] computed", snapshot);
        return snapshot;
    }, [L, session, user]);

    const clearState = useCallback(() => {
        L.log("[clearState] clearing in-memory state");
        setSession(null);
        setUser(null);
        setUserId(undefined);
        setRegistrationState({});
        setCurrentStepState(0);
        setError(null);
    }, [
        L,
        setCurrentStepState,
        setError,
        setRegistrationState,
        setSession,
        setUser,
        setUserId,
    ]);

    const hardLogout = useCallback(async () => {
        L.log("[hardLogout] start");
        await signOut();
        clearStoredTokens();
        emitAuthChange();
        L.log("[hardLogout] done");
    }, [L, emitAuthChange, signOut]);

    const dump = useCallback(() => {
        const snapshot = {
            location: {
                href: window.location.href,
                origin: window.location.origin,
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
            },
            session,
            user,
            userId,
            onboarding,
            currentStep: currentStepState,
            tokenStorage: readStoredTokens(),
        };

        L.log("[dump] snapshot", snapshot);
        return snapshot;
    }, [L, currentStepState, onboarding, session, user, userId]);

    return {
        devTokens,
        clearState,
        hardLogout,
        dump,
    };
};
